import { defineMessages } from 'react-intl';

export const scope = 'app.containers.ApprovalRules';

/**
 * recruiter-ui uses the legacy FormatJS extraction: manual dot-notation ids, and
 * `description` as the in-code record of what a string means. Both are required
 * here — an omitted id silently never reaches the translation file.
 *
 * Terminology is "rule", not "flow" (AXHE-4015, renamed per Michaela). The design
 * handoff predates that rename and still says "flow" throughout; the copy here
 * deliberately does not.
 */
export default defineMessages({
  // The surface is "Approvals" (AXHE-4472); "Approval rules" is now the name of
  // what one tab holds, not of the page. A separate id rather than a reworded
  // `pageTitle`, because the two say different things and a translated catalog
  // carries the old sentence forward under an unchanged id.
  pageHeading: {
    id: `${scope}.pageHeading`,
    defaultMessage: 'Approvals',
    description:
      'Title of the page where approvals are configured and reviewed; also the browser tab title',
  },
  pageSubtitle: {
    id: `${scope}.pageSubtitle`,
    defaultMessage:
      'Opening approvals and offer approvals allow more control over when an opening can become active or an offer can be extended by requiring it to be reviewed and approved by an assigned approver group.',
    description:
      'Explains, under the page title, what approvals do and why an admin would configure them',
  },
  backToSettings: {
    id: `${scope}.backToSettings`,
    defaultMessage: 'Back to settings',
    description:
      'Link above the page title, returning to the account settings area this page is reached from',
  },
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Approval rules',
    description:
      'Names the rules that decide when an offer or opening needs sign-off; the accessible name of the list while it loads',
  },
  approverRulesTab: {
    id: `${scope}.approverRulesTab`,
    defaultMessage: 'Approver rules',
    description:
      'Tab holding the rules that decide when an offer or opening needs sign-off',
  },
  offersTab: {
    id: `${scope}.offersTab`,
    defaultMessage: 'Offers',
    description: 'Filter showing only rules that gate offers to candidates',
  },
  openingsTab: {
    id: `${scope}.openingsTab`,
    defaultMessage: 'Openings',
    description: 'Filter showing only rules that gate job openings',
  },
  searchLabel: {
    id: `${scope}.searchLabel`,
    defaultMessage: 'Search rules',
    description: 'Label for the input that filters the rule list by name',
  },
  // Names the whole thing being created, because this button is the only entry
  // point to authoring one and "rule" alone does not say what kind. Sentence
  // case: the mock title-cases it, but every other button in the product is
  // sentence case and the design system says so explicitly.
  createRule: {
    id: `${scope}.createRule`,
    defaultMessage: 'Create approval rule',
    description:
      'Split button, with a caret, that opens the menu for starting a new approval rule',
  },
  levelCount: {
    id: `${scope}.levelCount`,
    defaultMessage:
      '{count, plural, one {# approval level} other {# approval levels}}',
    description:
      'How many sequential rounds of sign-off a rule requires before it is satisfied',
  },
  approverCount: {
    id: `${scope}.approverCount`,
    defaultMessage: '{count, plural, one {# approver} other {# approvers}}',
    description: 'How many people can sign off at a single round of approval',
  },
  disabledRule: {
    id: `${scope}.disabledRule`,
    defaultMessage: 'Off',
    description:
      'Badge on a rule that is configured but currently not gating anything',
  },
  disabledLevel: {
    id: `${scope}.disabledLevel`,
    defaultMessage: 'Off',
    description:
      'Badge on a level the rule skips. Separate from the rule badge with the same wording because the two label different objects and a translation may need to agree with a different noun',
  },
  managedElsewhere: {
    id: `${scope}.managedElsewhere`,
    defaultMessage: 'Managed on Opening Approvals',
    description:
      'Badge on a rule that was carried over from the older approvals page and can only be edited there',
  },
  managedElsewhereHint: {
    id: `${scope}.managedElsewhereHint`,
    defaultMessage:
      'This rule came from the Opening Approvals page. Edit it there — changes made here would be overwritten.',
    description:
      'Explains why a carried-over rule cannot be edited on this page and where to edit it instead',
  },
  // An account with no rules and a search that matched nothing are two states,
  // not one (AXHE-4468). Design Requirements §2 gives a single "No approval
  // rules found." covering both; `layout-composition` is explicit that they do
  // not collapse — "Empty state (no data) and search/filter zero-result state
  // are separate cases — provide distinct messaging for each" — and the two
  // genuinely have different ways out, one recoverable by clearing the search
  // and the other needing a rule created. Keep both. The doc's wording is also
  // a title with a full stop, which `copywriting-voice` does not allow.
  emptyTitle: {
    id: `${scope}.emptyTitle`,
    defaultMessage: 'No approval rules yet',
    description:
      'Heading shown when an account has not configured any approval rules',
  },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage:
      'Create a rule to require sign-off before an offer or opening goes out.',
    description:
      'Explains what creating an approval rule accomplishes, shown when none exist',
  },
  noSearchResultsTitle: {
    id: `${scope}.noSearchResultsTitle`,
    defaultMessage: 'No results for "{term}"',
    description:
      'Heading shown when a rule name search matched nothing, naming what was searched',
  },
  noSearchResultsBody: {
    id: `${scope}.noSearchResultsBody`,
    defaultMessage: 'Check the spelling or clear the search to see every rule.',
    description: 'Tells the user how to recover from a search that found nothing',
  },
  clearSearch: {
    id: `${scope}.clearSearch`,
    defaultMessage: 'Clear search',
    description:
      'Button in the no-results state that empties the search field, bringing the whole rule list back',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage: "We couldn't load your approval rules. Try again.",
    description:
      'Shown when the request for the rule list failed, with the recovery action',
  },
  retry: {
    id: `${scope}.retry`,
    defaultMessage: 'Try again',
    description: 'Button that re-requests the rule list after a failure',
  },

  // --- editor panel ---
  // The two items name the entity, not the rule: the button above them already
  // said "approval rule", so repeating it here would read "Create approval rule
  // › Offer rule". What the menu is actually asking is which entity the rule
  // gates, and a rule's subject type is fixed at creation — so this choice is
  // the one thing the menu exists to make.
  createOfferRule: {
    id: `${scope}.createOfferRule`,
    defaultMessage: 'Offer',
    description:
      'Menu choice that starts a rule gating offers sent to candidates',
  },
  createOpeningRule: {
    id: `${scope}.createOpeningRule`,
    defaultMessage: 'Opening',
    description: 'Menu choice that starts a rule gating job openings',
  },
  newOfferRuleTitle: {
    id: `${scope}.newOfferRuleTitle`,
    defaultMessage: 'New offer rule',
    description: 'Heading of the panel where a new offer-gating rule is written',
  },
  newOpeningRuleTitle: {
    id: `${scope}.newOpeningRuleTitle`,
    defaultMessage: 'New opening rule',
    description:
      'Heading of the panel where a new opening-gating rule is written',
  },
  editRuleTitle: {
    id: `${scope}.editRuleTitle`,
    defaultMessage: 'Edit rule',
    description: 'Heading of the panel where an existing rule is changed',
  },
  ruleNameLabel: {
    id: `${scope}.ruleNameLabel`,
    defaultMessage: 'Rule name',
    description: 'Label for the field naming an approval rule',
  },
  // Design Requirements §3 words this "Rule name is required" (AXHE-4468). It
  // stays phrased as the action instead: `constraint-anti-pattern-guard`
  // prohibits an error that only describes what went wrong, and
  // `copywriting-voice`'s error table gives this exact pair — "Required field"
  // ✗ / "Enter a job title" ✓. §3 asks for two things, and the other one is
  // now built: the field is marked required on its label, which is where "is
  // required" is said. This message is the part that says what to do about it.
  ruleNameRequired: {
    id: `${scope}.ruleNameRequired`,
    defaultMessage: 'Enter a rule name',
    description:
      'Validation message when the rule name field was left empty, phrased as the action to take',
  },
  levelHeading: {
    id: `${scope}.levelHeading`,
    defaultMessage: 'Level {number}',
    description:
      'Heading of one round of sign-off within a rule, numbered by its order',
  },
  levelNameLabel: {
    id: `${scope}.levelNameLabel`,
    defaultMessage: 'Level name',
    description: 'Label for the field naming one round of sign-off',
  },
  levelOff: {
    id: `${scope}.levelOff`,
    defaultMessage:
      'This level is off, so the rule skips it. Saving keeps it off — levels are no longer switched on and off here.',
    description:
      'Read-only notice on a level that was switched off before per-level toggles were removed, saying that the rule ignores it and that saving does not change that',
  },
  approversLabel: {
    id: `${scope}.approversLabel`,
    defaultMessage: 'Approvers',
    description: 'Label for the people who can sign off at this round',
  },
  // Design Requirements §3 words this "Each level requires at least one
  // approver." (AXHE-4468). "needs" and "requires" say the same thing, so the
  // only real difference is the full stop, and `copywriting-voice`'s rule is
  // one sentence takes none, two or more take punctuation throughout — which
  // is exactly how this file already splits: the single-sentence ones below
  // carry no stop, `conditionFieldUnavailable` and `conditionOperatorUnavailable`
  // are two sentences and do. This is one sentence. Not worth orphaning five
  // translated catalogs to add a period the guide asks us not to have.
  approverRequired: {
    id: `${scope}.approverRequired`,
    defaultMessage: 'Each level needs at least one approver',
    description:
      'Validation message when a round of sign-off was left with nobody able to approve it',
  },
  offboardedApprovers: {
    id: `${scope}.offboardedApprovers`,
    defaultMessage:
      '{count, plural, one {# approver is no longer an active user} other {# approvers are no longer active users}} and will be removed from this level when you save',
    description:
      'Warning shown when a level lists approvers whose user accounts have been deactivated, so an admin knows that saving drops them',
  },
  addLevel: {
    id: `${scope}.addLevel`,
    defaultMessage: 'Add level',
    description: 'Button that appends another round of sign-off to the rule',
  },
  removeLevel: {
    id: `${scope}.removeLevel`,
    defaultMessage: 'Remove level',
    description: 'Button that deletes one round of sign-off from the rule',
  },
  reorderLevel: {
    id: `${scope}.reorderLevel`,
    defaultMessage: 'Reorder level {number}',
    description:
      'Accessible label for the handle that moves one round of sign-off earlier or later in the sequence. Says "reorder" rather than "drag" because the handle is operated by keyboard as well',
  },
  saveRule: {
    id: `${scope}.saveRule`,
    defaultMessage: 'Save rule',
    description: 'Button that writes the rule being edited',
  },
  conditionsPreserved: {
    id: `${scope}.conditionsPreserved`,
    defaultMessage:
      "Data-key conditions on each level are edited above. Other conditions, and this rule's own scope conditions, are kept as they are and edited elsewhere.",
    description:
      "Clarifies that only a level's data-key conditions are edited in this panel; other condition types and the rule's own scope conditions are carried over untouched rather than being lost on save",
  },
  saveFailed: {
    id: `${scope}.saveFailed`,
    defaultMessage: "We couldn't save this rule. Try again.",
    description:
      'Shown when writing the rule failed, with the recovery action',
  },

  // --- row actions ---
  ruleActiveToggle: {
    id: `${scope}.ruleActiveToggle`,
    defaultMessage: 'Active',
    description:
      'Switch on a rule row controlling whether it currently gates anything',
  },
  deleteRule: {
    id: `${scope}.deleteRule`,
    defaultMessage: 'Delete rule',
    description: 'Button that permanently removes an approval rule',
  },
  // Names the action, not the rule; the rule is named in the body sentence
  // instead. This is the design's, and nothing in the shell forces it — an
  // earlier revision of this dialog justified it by FDS `ModalHeader`'s
  // `noWrap`, which would have truncated a long account-authored rule name in
  // the heading, but that shell is gone and the heading here wraps. Recorded
  // because the reason is worth having on the record accurately: a short,
  // constant title reads as the action and leaves the rule name somewhere it
  // can wrap, which is a preference rather than a constraint (AXHE-4553).
  deleteRuleTitle: {
    id: `${scope}.deleteRuleTitle`,
    defaultMessage: 'Delete rule',
    description:
      'Heading of the confirmation asking whether to permanently remove an approval rule',
  },
  // Opens on the consequence, not on "Are you sure you want to" — the standard
  // in `.claude/skills/ux-design/copywriting-voice` names that opener as the
  // anti-pattern, and nothing here forces it: the name sits in the same
  // wrappable position either way. The prototype's own wording carries the
  // filler; AXHE-4553 holds that difference open for Design.
  deleteRuleCascade: {
    id: `${scope}.deleteRuleCascade`,
    defaultMessage:
      'This permanently removes <b>{name}</b> and {count, plural, one {its # level} other {all # levels}} within it.',
    description:
      'Body of the delete confirmation. Names the rule being deleted and what else is destroyed alongside it, so the scale of the deletion is clear before confirming. <b> marks the rule name, which is the one part an admin has to check before confirming',
  },
  // Two consequences, in the order an admin can act on them: the requests that
  // stop, and what then happens to the subjects behind them. The second sentence
  // is the one the design added — an approval that is cancelled does not hold
  // its subject back, so it goes out unapproved unless another rule catches it.
  //
  // `subject` selects the noun because one rule table backs both filters: a rule
  // is either offers or openings, never both, and the design requirements spell
  // the consequence "those offers/openings" precisely because the dialog serves
  // whichever one is open. Printing both nouns on a surface the admin has
  // already filtered would be vaguer than the rule itself is.
  deleteRulePending: {
    id: `${scope}.deleteRulePending`,
    defaultMessage:
      'Deleting this rule will <b>cancel {count, plural, one {# pending approval request} other {# pending approval requests}}</b> currently using it. If no other rule applies, {subject, select, Funnel {those openings} other {those offers}} will pass through as <b>not needing approval</b>.',
    description:
      'Warning that the offers or openings currently awaiting sign-off under this rule stop being gated by it, and go out unapproved unless another rule applies. {subject} is the rule\'s subject type, which decides whether the sentence names offers or openings. <b> marks the count and the outcome, the two parts that change what the admin decides',
  },
  // Its own line rather than the tail of the body sentence: the design puts the
  // permanence note under the warning so the warning is what the eye lands on,
  // and a sentence about levels is not where an admin looks for "this is final".
  //
  // Worded as the standard spells it rather than the prototype's "This action
  // cannot be undone." — same sentence, house contraction (AXHE-4553).
  deleteRuleUndone: {
    id: `${scope}.deleteRuleUndone`,
    defaultMessage: "This can't be undone.",
    description:
      'Footnote under the delete confirmation, stating that the deletion is permanent',
  },
  toggleFailed: {
    id: `${scope}.toggleFailed`,
    defaultMessage: "We couldn't change this rule. Try again.",
    description:
      'Shown when switching a rule active or inactive failed, with the recovery action',
  },
  deleteFailed: {
    id: `${scope}.deleteFailed`,
    defaultMessage: "We couldn't delete this rule. Try again.",
    description: 'Shown when removing a rule failed, with the recovery action',
  },
  // --- condition builder (AXHE-3929) ---
  conditionsLabel: {
    id: `${scope}.conditionsLabel`,
    defaultMessage: "Conditions",
    description:
      "Heading for the rules deciding whether this level of sign-off applies at all",
  },
  addCondition: {
    id: `${scope}.addCondition`,
    defaultMessage: "Add condition",
    description: "Button that adds another condition to this level",
  },
  removeCondition: {
    id: `${scope}.removeCondition`,
    defaultMessage: "Remove condition",
    description: "Button that deletes one condition from this level",
  },
  conditionFieldLabel: {
    id: `${scope}.conditionFieldLabel`,
    defaultMessage: "Field",
    description: "Label for the data the condition is written about",
  },
  conditionOperatorLabel: {
    id: `${scope}.conditionOperatorLabel`,
    defaultMessage: "Comparison",
    description: "Label for how the field is compared, such as greater than",
  },
  conditionValueLabel: {
    id: `${scope}.conditionValueLabel`,
    defaultMessage: "Value",
    description: "Label for the literal the field is compared against",
  },
  conditionCompareKeyLabel: {
    id: `${scope}.conditionCompareKeyLabel`,
    defaultMessage: "Compare with field",
    description:
      "Label for the second field this condition compares against instead of a fixed value",
  },
  compareToFieldToggle: {
    id: `${scope}.compareToFieldToggle`,
    defaultMessage: "Compare with another field",
    description:
      "Switches the condition from comparing against a typed-in value to comparing against a second field",
  },
  conditionNeedsField: {
    id: `${scope}.conditionNeedsField`,
    defaultMessage: "Choose a field",
    description: "Validation message when a condition has no field selected",
  },
  conditionNeedsOperator: {
    id: `${scope}.conditionNeedsOperator`,
    defaultMessage: "Choose a comparison",
    description: "Validation message when a condition has no comparison selected",
  },
  conditionNeedsValue: {
    id: `${scope}.conditionNeedsValue`,
    defaultMessage: "Enter a value to compare against",
    description: "Validation message when a condition has nothing to compare the field with",
  },
  conditionNeedsCompareKey: {
    id: `${scope}.conditionNeedsCompareKey`,
    defaultMessage: "Choose a field to compare with",
    description:
      "Validation message when a condition compares against another field but none was selected",
  },
  noComparableFields: {
    id: `${scope}.noComparableFields`,
    defaultMessage: "No other field has the same type as this one",
    description:
      "Explains why the compare-with-field list is empty: comparisons are only offered between fields holding the same kind of data",
  },
  conditionsLoading: {
    id: `${scope}.conditionsLoading`,
    defaultMessage: "Loading fields",
    description:
      "Shown while the list of fields a condition can be written about is still being fetched",
  },
  conditionsLoadError: {
    id: `${scope}.conditionsLoadError`,
    defaultMessage:
      "We couldn't load the fields conditions are written about. Conditions on this rule are unchanged.",
    description:
      "Shown when the field catalog failed to load: conditions cannot be edited, and the ones already on the rule are left as they are",
  },
  conditionFieldUnavailable: {
    id: `${scope}.conditionFieldUnavailable`,
    defaultMessage: "This field is no longer available. Choose another.",
    description:
      "Validation message when a saved condition names a field the account no longer has",
  },
  conditionOperatorUnavailable: {
    id: `${scope}.conditionOperatorUnavailable`,
    defaultMessage: "This comparison no longer applies to this field. Choose another.",
    description:
      "Validation message when a saved condition uses a comparison the field's type does not allow, usually because the field's type changed",
  },

  // --- operator labels (AXHE-4473) ---
  // The server sends its own tokens (`greater_than`); these are what an admin
  // reads. Phrased to complete the sentence the row spells out left to right —
  // "Sign-on bonus / greater than / 10000" — so each one is a verb phrase in
  // lower case rather than a title-cased label.
  operatorEquals: {
    id: `${scope}.operatorEquals`,
    defaultMessage: 'equals',
    description:
      "Comparison choice: the field's value is exactly the value given",
  },
  operatorNotEquals: {
    id: `${scope}.operatorNotEquals`,
    defaultMessage: 'does not equal',
    description:
      "Comparison choice: the field's value is anything other than the value given",
  },
  operatorContains: {
    id: `${scope}.operatorContains`,
    defaultMessage: 'contains',
    description:
      "Comparison choice: the field's text includes the value given somewhere within it",
  },
  operatorGreaterThan: {
    id: `${scope}.operatorGreaterThan`,
    defaultMessage: 'greater than',
    description:
      "Comparison choice: the field's value is above the value given",
  },
  operatorLessThan: {
    id: `${scope}.operatorLessThan`,
    defaultMessage: 'less than',
    description:
      "Comparison choice: the field's value is below the value given",
  },
  operatorIsPresent: {
    id: `${scope}.operatorIsPresent`,
    defaultMessage: 'is present',
    description:
      'Comparison choice: the field has any value at all, whatever it is',
  },

  // --- how conditions combine (AXHE-3930's UI half) ---
  // Sentence case rather than `globalMessages.and` / `.or`, which are "AND" and
  // "OR" — those are used as inline connectors in older surfaces, and a pill is
  // sentence case.
  conditionLogicAnd: {
    id: `${scope}.conditionLogicAnd`,
    defaultMessage: 'And',
    description:
      'Connector between two conditions, meaning both of them have to be true for the level to apply',
  },
  conditionLogicOr: {
    id: `${scope}.conditionLogicOr`,
    defaultMessage: 'Or',
    description:
      'Connector between two conditions, meaning either one of them being true is enough for the level to apply',
  },
  conditionLogicToggle: {
    id: `${scope}.conditionLogicToggle`,
    defaultMessage:
      'Conditions on this level combine with {current}. Switch to {other}',
    description:
      'Accessible name of the connector between two conditions, saying how they currently combine and what clicking it changes them to',
  },
});
